import { axios } from "src/api/axios";
import { logAxiosError } from "src/redux/notification";
import { store } from "src/redux/store";

const AuthService = {
  googleLogin,
  login,
  signup,
  onAuth,
  guessMe,
  insertIntoCohort,
  requestResetPassword,
  resetPassword,
  adminResetPassword,
  updateIsMarketingEmailEnabled,
};

export default AuthService;

interface GoogleLoginResponse {
  user: User;
  is_found: boolean;
}

async function googleLogin(token: string) {
  try {
    const resp = await axios.post<GoogleLoginResponse>(
      "/api/users/google_login",
      {
        token,
      }
    );
    return resp.data;
  } catch (err: any) {
    store.dispatch(logAxiosError(err, "Logging in with Google user"));
    throw err;
  }
}

interface LoginResponse {
  user?: User;
  is_active?: boolean;
}

async function login(email: string, password: string) {
  try {
    const resp = await axios.post<LoginResponse>("/api/users/login", {
      email,
      password,
    });
    return resp.data;
  } catch (err: any) {
    store.dispatch(logAxiosError(err, "Logging in with username and password"));
    throw err;
  }
}

interface SignupResponse {
  state: "not_found" | "emailed" | "activated";
}

async function signup(email: string) {
  try {
    const resp = await axios.post<SignupResponse>("/api/users/signup", {
      email,
    });
    return resp.data;
  } catch (err: any) {
    store.dispatch(logAxiosError(err, "Signing up with email"));
    throw err;
  }
}

async function guessMe(user: Pick<User, "id" | "type">) {
  try {
    const resp = await axios.post<User>("/api/users/me", { user });
    return resp.data;
  } catch (err: any) {
    store.dispatch(logAxiosError(err, "Fetching user session"));
    throw err;
  }
}

interface OnAuthRequest {
  id: string;
  invite_token?: string;
  default_event?: string;
  route?: string;
  email?: string;
}

// Empty response body expected
interface OnAuthResponse {}

async function onAuth(authParams: OnAuthRequest): Promise<OnAuthResponse> {
  try {
    const resp = await axios.post<OnAuthResponse>("/api/on_auth", {
      ...authParams,
    });

    return resp.data;
  } catch (err: any) {
    store.dispatch(logAxiosError(err, "Preparing to authenticate"));
    throw err;
  }
}

interface InsertIntoCohortResponse {
  successful_inserts: number;
  failed_inserts: number;
  overflowed_inserts: number;
}

async function insertIntoCohort(cohortId: number, users: MappedPerson[]) {
  try {
    const resp = await axios.post<InsertIntoCohortResponse>(
      "/api/users/cohort_insert",
      {
        cohortId,
        users,
      }
    );

    return resp.data;
  } catch (err: any) {
    store.dispatch(logAxiosError(err, "Inserting users into cohort"));
    throw err;
  }
}

interface RequestResetPasswordResponse {
  id: string;
}

async function requestResetPassword(email: string) {
  try {
    const resp = await axios.post<RequestResetPasswordResponse>(
      "/api/users/request_reset_password",
      {
        email,
      }
    );

    return resp.data;
  } catch (err: any) {
    store.dispatch(logAxiosError(err, "Resetting password"));
    throw err;
  }
}

async function resetPassword(token_id: string, new_password: string) {
  try {
    const resp = await axios.post<RequestResetPasswordResponse>(
      "/api/users/reset_password",
      {
        token_id,
        new_password,
      }
    );

    return resp.data;
  } catch (err: any) {
    store.dispatch(logAxiosError(err, "Resetting password"));
    throw err;
  }
}

async function adminResetPassword(user_id: string, new_password: string) {
  try {
    const resp = await axios.post<RequestResetPasswordResponse>(
      "/api/users/reset_password",
      {
        user_id,
        new_password,
      }
    );

    return resp.data;
  } catch (err: any) {
    store.dispatch(logAxiosError(err, "Resetting password"));
    throw err;
  }
}

interface UpdateIsMarketingEmailEnabledResponse {
  user: User;
}

async function updateIsMarketingEmailEnabled(
  is_marketing_email_enabled: boolean
) {
  try {
    const resp = await axios.post<UpdateIsMarketingEmailEnabledResponse>(
      "/api/users/update_is_marketing_email_enabled",
      {
        is_marketing_email_enabled,
      }
    );

    return resp.data;
  } catch (err: any) {
    store.dispatch(logAxiosError(err, "Updating marketing email settings"));
    throw err;
  }
}
